import React, { useState } from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Space,
  Collapse,
  Button,
  Typography,
  Divider,
  Tooltip,
  message,
  Badge,
  Empty,
} from 'antd';
import {
  FileTextOutlined,
  BankOutlined,
  UserOutlined,
  CalendarOutlined,
  CopyOutlined,
  StarOutlined,
  BookOutlined,
  CheckCircleOutlined,
  HighlightOutlined,
} from '@ant-design/icons';
import type { AnnotationData, QuestionDetail, ReplyDetail } from '../../../types';


const { Title, Text, Paragraph } = Typography;

interface CaseDetailViewProps {
  caseData: AnnotationData | null;
  onCopyTemplate?: (content: string) => void;
  onFavorite?: (content: string) => void;
}

// 高亮法规引用
const highlightLaws = (text: string, laws: { name: string }[]) => {
  if (!text || !laws || laws.length === 0) return text;

  let highlighted = text;
  laws.forEach((law) => {
    const regex = new RegExp(law.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    highlighted = highlighted.replace(
      regex,
      `<span class="text-green-600 bg-green-100 px-1 rounded font-medium">${law.name}</span>`
    );
  });
  return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
};



// QA对照项组件
const QAItem: React.FC<{
  question: QuestionDetail;
  reply: ReplyDetail | undefined;
  index: number;
  onCopyTemplate: (content: string) => void;
  onFavorite: (content: string) => void;
}> = ({ question, reply, index, onCopyTemplate, onFavorite }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!reply) {
    return (
      <Alert
        message="未找到对应的答复项"
        type="warning"
        showIcon
        className="mb-4"
      />
    );
  }

  // 核心论据关键词
  const keyArguments = ['依据', '根据', '因此', '故', '综上所述'];

  return (
    <div className="mb-6 border rounded-lg overflow-hidden">
      {/* 标题栏 */}
      <div
        className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Badge count={index + 1} style={{ backgroundColor: '#1890ff' }} />
          <Tag color="blue">{question.topic}</Tag>
          <Text className="text-sm">{question.question_summary}</Text>
        </div>
        <Tag
          color={
            reply.reply_position === '不采纳'
              ? 'red'
              : reply.reply_position === '部分采纳'
              ? 'orange'
              : reply.reply_position === '完全采纳'
              ? 'green'
              : 'blue'
          }
        >
          {reply.reply_position}
        </Tag>
      </div>

      {/* 左右对照内容 */}
      {isExpanded && (
        <div className="grid grid-cols-2 divide-x">
          {/* 左侧：质疑 */}
          <div className="p-4 bg-red-50/30">
            <div className="flex items-center gap-2 mb-3 text-red-600">
              <FileTextOutlined />
              <span className="font-medium">质疑内容</span>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <div className="text-gray-500 mb-1">质疑摘要</div>
                <div className="font-medium">{question.question_summary}</div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">详细内容</div>
                <Paragraph className="text-gray-700 bg-white p-3 rounded">
                  {question.question_detail}
                </Paragraph>
              </div>

              {question.evidence_facts && question.evidence_facts.length > 0 && (
                <div>
                  <div className="text-gray-500 mb-1">事实依据</div>
                  <ul className="list-disc list-inside text-gray-700 bg-white p-3 rounded">
                    {question.evidence_facts.map((fact, idx) => (
                      <li key={idx}>{fact}</li>
                    ))}
                  </ul>
                </div>
              )}

              {question.laws_regulations_cited &&
                question.laws_regulations_cited.length > 0 && (
                  <div>
                    <div className="text-gray-500 mb-1">援引法规</div>
                    <div className="flex flex-wrap gap-1">
                      {question.laws_regulations_cited.map((law, idx) => (
                        <Tag key={idx} color="red">
                          <BookOutlined /> {law.name}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

              <div>
                <div className="text-gray-500 mb-1">请求措施</div>
                <div className="text-primary font-medium">
                  {question.requested_action}
                </div>
              </div>
            </div>
          </div>

          {/* 右侧：答复 */}
          <div className="p-4 bg-green-50/30">
            <div className="flex items-center gap-2 mb-3 text-green-600">
              <CheckCircleOutlined />
              <span className="font-medium">答复内容</span>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <div className="text-gray-500 mb-1">质疑点复述</div>
                <div className="text-gray-700">{reply.question_summary}</div>
              </div>

              {reply.tender_clauses_cited &&
                reply.tender_clauses_cited.length > 0 && (
                  <div>
                    <div className="text-gray-500 mb-1">招标文件依据</div>
                    <ul className="list-disc list-inside text-gray-700 bg-white p-3 rounded">
                      {reply.tender_clauses_cited.map((clause, idx) => (
                        <li key={idx}>{clause}</li>
                      ))}
                    </ul>
                  </div>
                )}

              {reply.laws_regulations_cited &&
                reply.laws_regulations_cited.length > 0 && (
                  <div>
                    <div className="text-gray-500 mb-1">援引法规</div>
                    <div className="space-y-1">
                      {reply.laws_regulations_cited.map((law, idx) => (
                        <div key={idx} className="bg-white p-2 rounded">
                          <Tag color="green">
                            <BookOutlined /> {law.name}
                          </Tag>
                          {law.articles && (
                            <div className="text-xs text-gray-500 mt-1 ml-2">
                              {law.articles.map((a) => a.article_index).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              <div>
                <div className="text-gray-500 mb-1">
                  <HighlightOutlined className="mr-1" />
                  回复理由/论证
                </div>
                <Paragraph className="text-gray-700 bg-white p-3 rounded leading-relaxed">
                  {highlightLaws(reply.reply_reasoning, reply.laws_regulations_cited || [])}
                </Paragraph>
                {/* 核心论据高亮 */}
                <div className="mt-2 flex flex-wrap gap-1">
                  {keyArguments.map((kw) =>
                    reply.reply_reasoning?.includes(kw) ? (
                      <Tag key={kw} color="blue">
                        {kw}
                      </Tag>
                    ) : null
                  )}
                </div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">最终结论</div>
                <div className="text-green-700 font-medium bg-white p-3 rounded border-l-4 border-green-500">
                  {reply.reply_conclusion}
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex gap-2 pt-2">
                <Tooltip title="复制为模板">
                  <Button
                   
                    icon={<CopyOutlined />}
                    onClick={() => onCopyTemplate(reply.reply_reasoning)}
                  >
                    复制模板
                  </Button>
                </Tooltip>
                <Tooltip title="收藏精彩话术">
                  <Button
                   
                    icon={<StarOutlined />}
                    onClick={() => onFavorite(reply.reply_conclusion)}
                  >
                    收藏
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 导入 Alert
import { Alert } from 'antd';

const CaseDetailView: React.FC<CaseDetailViewProps> = ({
  caseData,
  onCopyTemplate,
  onFavorite,
}) => {
  if (!caseData) {
    return (
      <Empty
        description="请选择案例查看详情"
        className="py-20"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const handleCopyTemplate = (content: string) => {
    navigator.clipboard.writeText(content);
    message.success('已复制到剪贴板');
    onCopyTemplate?.(content);
  };

  const handleFavorite = (content: string) => {
    message.success('已收藏');
    onFavorite?.(content);
  };

  // 按 topic 分组 QA
  const qaPairs = caseData.questionDetails.map((q) => ({
    question: q,
    reply: caseData.replyDetails.find((r) => r.related_inquiry_item_id === q.item_id),
  }));

  return (
    <div className="h-full overflow-auto">
      {/* 元数据概览 */}
      <Card className="mb-4" title="📋 案例概览">
        <Descriptions column={2}>
          <Descriptions.Item label="案件ID">
            <span className="font-mono">{caseData.docMeta.case_id}</span>
          </Descriptions.Item>
          <Descriptions.Item label="文档类型">
            {caseData.docMeta.doc_type}
          </Descriptions.Item>
          <Descriptions.Item label="项目名称" span={2}>
            <Title level={5} className="!mb-0">
              {caseData.docMeta.project_name}
            </Title>
          </Descriptions.Item>
          <Descriptions.Item label="招标人">
            <Space>
              <BankOutlined />
              {caseData.docMeta.responder?.name}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="发文日期">
            <Space>
              <CalendarOutlined />
              {caseData.docMeta.issue_date}
            </Space>
          </Descriptions.Item>
          {caseData.docMeta.questioner && (
            <Descriptions.Item label="投标人">
              <Space>
                <UserOutlined />
                {caseData.docMeta.questioner.name}
              </Space>
            </Descriptions.Item>
          )}
          {caseData.docMeta.overall_decision && (
            <Descriptions.Item label="整体结论">
              <Tag
               
                color={
                  caseData.docMeta.overall_decision === '维持原判'
                    ? 'red'
                    : caseData.docMeta.overall_decision === '部分采纳'
                    ? 'orange'
                    : caseData.docMeta.overall_decision === '完全采纳'
                    ? 'green'
                    : 'blue'
                }
              >
                {caseData.docMeta.overall_decision}
              </Tag>
            </Descriptions.Item>
          )}
        </Descriptions>

        {caseData.docMeta.intent_summary && (
          <>
            <Divider className="my-3" />
            <div>
              <div className="text-gray-500 mb-1">意图摘要</div>
              <Paragraph>{caseData.docMeta.intent_summary}</Paragraph>
            </div>
          </>
        )}
      </Card>

      {/* QA 对照列表 */}
      <Card
        title={
          <div className="flex items-center justify-between">
            <span>📑 质疑-答复对照 ({qaPairs.length} 对)</span>
            <Space>
              <Tag color="blue">{caseData.questionDetails.length} 质疑</Tag>
              <Tag color="green">{caseData.replyDetails.length} 答复</Tag>
            </Space>
          </div>
        }
        className="mb-4"
      >
        {qaPairs.map((pair, index) => (
          <QAItem
            key={pair.question.item_id}
            question={pair.question}
            reply={pair.reply}
            index={index}
            onCopyTemplate={handleCopyTemplate}
            onFavorite={handleFavorite}
          />
        ))}
      </Card>

      {/* NER 实体 */}
      {caseData.entities && caseData.entities.length > 0 && (
        <Card
          title="🏷️ 实体标注"
         
          className="mb-4"
        >
          <div className="flex flex-wrap gap-2">
            {caseData.entities.map((entity) => (
              <Tag
                key={entity.id}
                color="blue"
                className="text-sm"
              >
                {entity.text}
                <span className="text-gray-400 ml-1">[{entity.label}]</span>
              </Tag>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default CaseDetailView;
